import React, { createContext, useContext, useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { APP_URL } from '../env';

interface AuthContextType {
  token: string | null;
  setToken: (token: string | null) => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkToken = () => {
      const cookies = document.cookie.split(';');
      let foundToken = null;
      for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.startsWith('accessToken=')) {
          foundToken = cookie.substring('accessToken='.length);
          break;
        }
      }

      if (foundToken) {
        setToken(foundToken);
        setIsLoading(false);
      } else {
        window.location.href = `${APP_URL}/login`;
      }
    };

    checkToken();

    // Check token every minute
    const interval = setInterval(checkToken, 60000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={{ token, setToken, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
